import { cn } from '../../../lib/cn'
import { data, dataHoraPorExtenso } from '../../../lib/datas'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import type { ResumoChamado } from '../../../types/chamado'
import { nomeCurtoDoRamo } from './ramo'

/**
 * As marcas que a tabela e os cartões do celular precisam mostrar do mesmo jeito.
 *
 * Ficam num arquivo só porque as duas apresentações da MESMA lista não podem divergir: é no
 * telefone que o fiscal confere correndo, e uma marca que só existe na tabela é uma marca que
 * metade dos acessos não vê.
 */

/**
 * Campo que a API pode não mandar.
 *
 * "Não informado" escrito por extenso, e não uma célula em branco: célula vazia numa tabela é
 * ambígua — o usuário não sabe se ninguém preencheu ou se o sistema perdeu o dado. E aqui isso
 * acontece de verdade: entidade e grupo vêm em branco em chamado que a planilha não classificou.
 *
 * @param props o texto vindo da API
 * @returns o texto, ou o aviso de campo vazio
 */
export function ValorOuVazio({ texto }: { texto: string | null }) {
  if (!texto) {
    return <span className="whitespace-nowrap text-slate-400">{TEXTOS.naoInformado}</span>
  }
  return <>{texto}</>
}

/**
 * Entidade ou grupo atendente pelo ramo final da árvore, com o caminho inteiro no `title`.
 *
 * Ver {@link nomeCurtoDoRamo}: a raiz igual em todas as linhas sai, e o que distingue um chamado
 * do outro passa a caber numa linha. Cortar não esconde nada: o caminho completo fica no `title` e
 * o cartão do celular continua trazendo o campo inteiro.
 *
 * **A largura mora neste `<span>`, e não no `<td>`.** Com `table-layout` automático o navegador
 * trata `max-width` em célula como sugestão; num elemento de bloco dentro dela, vale. E sem `block`
 * junto: o `line-clamp` já produz a caixa de bloco, e `.block` venceria o `display` dele.
 *
 * @param props o caminho vindo da API
 * @returns o nome curto, ou o aviso de campo vazio
 */
export function RamoDaArvore({ caminho }: { caminho: string | null }) {
  if (!caminho) {
    return <span className="whitespace-nowrap text-slate-400">{TEXTOS.naoInformado}</span>
  }
  return (
    <span className="line-clamp-2 max-w-[12rem] break-words" title={caminho}>
      {nomeCurtoDoRamo(caminho)}
    </span>
  )
}

/**
 * O fechamento, ou a frase que explica a ausência dele.
 *
 * Chamado sem fechamento não é dado faltando: é chamado aberto — e é a diferença que decide de qual
 * mês ele é, porque a competência é o mês do fechamento. Um travessão na coluna deixaria as duas
 * situações com a mesma cara, e o chamado aberto pareceria um registro incompleto.
 *
 * @param props o instante de fechamento, quando houver
 * @returns a data do fechamento, ou "ainda aberto"
 */
export function Fechamento({ fechadoEm }: { fechadoEm: string | null }) {
  if (!fechadoEm) {
    return <span className="text-slate-500">Ainda aberto</span>
  }
  return (
    <time dateTime={fechadoEm} title={dataHoraPorExtenso(fechadoEm)}>
      {data(fechadoEm)}
    </time>
  )
}

/**
 * O chamado já passou pela análise do fiscal?
 *
 * **Duas palavras, e nenhuma delas é uma cor sozinha.** "Sem julgamento" em âmbar com o texto ao
 * lado, "Julgado" em verde com o texto ao lado — quem não distingue as duas cores lê a mesma coisa.
 *
 * Não é alerta e não é erro: um chamado por julgar é trabalho a fazer, não defeito. O que ele
 * significa está no `title` e no chip de triagem, com a frase inteira.
 *
 * @param props se o chamado já foi julgado
 * @returns a etiqueta do julgamento
 */
export function Julgamento({ julgado }: { julgado: boolean }) {
  const cores = julgado ? 'bg-green-50 text-green-900 ring-green-200' : 'bg-amber-50 text-amber-900 ring-amber-200'
  return (
    <span
      title={julgado ? undefined : TEXTOS.chamados.semJulgamentoAjuda}
      className={cn('inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1', cores)}
    >
      {julgado ? TEXTOS.chamados.julgado : TEXTOS.chamados.semJulgamento}
    </span>
  )
}

/**
 * O código da solicitação de mudança vinculada, quando há.
 *
 * Texto e não link: a listagem recebe o código da GMUD, mas não o identificador dela — e um link
 * montado a partir do código levaria a um endereço que não existe. O caminho para a solicitação
 * está no detalhe do chamado, onde a API manda os dois.
 *
 * @param props a linha da listagem
 * @returns a marca da GMUD vinculada, ou nada
 */
export function GmudVinculada({ chamado }: { chamado: ResumoChamado }) {
  if (!chamado.gmudCodigo) {
    return null
  }
  return (
    <span className="mt-0.5 block text-xs text-slate-500">
      {TERMOS.gmud.sigla}: <span className="font-medium text-slate-700">{chamado.gmudCodigo}</span>
    </span>
  )
}
